import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, GeoJSON, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { projectStatusLabel } from '../lib/workStatus';
import { WmsLayers } from './map/WmsLayers';
import { FellingAreasLayer } from './map/FellingAreasLayer';

export const STATUS_PIN_COLORS = {
  not_started: '#9ca3af',
  in_progress: '#d97706',
  completed: '#16a34a',
  verified: '#0d9488',
  delivered: '#2563eb',
  closed: '#475569',
  on_hold: '#ea580c',
  active: '#d97706'
};

const LATVIA_CENTER = [56.95, 24.6];

const pinIcon = (color, selected) => {
  const size = selected ? 40 : 30;
  const height = Math.round(size * 1.4);
  return L.divIcon({
    className: 'sila-pin',
    html: `<svg width="${size}" height="${height}" viewBox="0 0 30 42" style="filter:drop-shadow(0 3px 3px rgba(0,0,0,.35));transition:transform .2s"><path d="M15 0C6.7 0 0 6.7 0 15c0 10 15 27 15 27s15-17 15-27C30 6.7 23.3 0 15 0z" fill="${color}" stroke="${selected ? '#1F3D2B' : '#fff'}" stroke-width="${selected ? 3 : 2}"/><circle cx="15" cy="15" r="6" fill="#fff"/></svg>`,
    iconSize: [size, height],
    iconAnchor: [size / 2, height],
    popupAnchor: [0, -height + 6]
  });
};

const FitToMarkers = ({ points }) => {
  const map = useMap();
  const fitted = useRef(false);
  useEffect(() => {
    if (fitted.current) return;
    if (points.length === 0) {
      map.setView(LATVIA_CENTER, 7);
      fitted.current = true;
      return;
    }
    map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 12 });
    fitted.current = true;
  }, [points, map]);
  return null;
};

const FlyToSelected = ({ target }) => {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo(target, Math.max(map.getZoom(), 11), { duration: 0.8 });
  }, [target, map]);
  return null;
};

const ClickCapture = ({ active, onPick }) => {
  const map = useMap();
  useEffect(() => {
    map.getContainer().style.cursor = active ? 'crosshair' : '';
  }, [active, map]);
  useMapEvents({ click: (e) => { if (active) onPick(e.latlng); } });
  return null;
};

export const ProjectsLeafletMap = ({ projects, boundaries = [], fellingAreas = [], wmsLayers = [], clientNames, selectedId, onSelect, placingActive, onPlace, onMove, onOpenProject, t }) => {
  const markerRefs = useRef({});
  const located = projects.filter((p) => p.latitude != null && p.longitude != null);
  const points = located.map((p) => [p.latitude, p.longitude]);
  const selected = located.find((p) => p.id === selectedId);
  const visibleIds = new Set(projects.map((p) => p.id));
  const visibleBoundaries = boundaries.filter((b) => visibleIds.has(b.project_id));
  const visibleAreas = fellingAreas.filter((a) => visibleIds.has(a.project_id));

  useEffect(() => {
    if (selectedId && markerRefs.current[selectedId]) {
      setTimeout(() => markerRefs.current[selectedId]?.openPopup(), 850);
    }
  }, [selectedId]);

  return (
    <MapContainer center={LATVIA_CENTER} zoom={7} scrollWheelZoom className="w-full h-full rounded-2xl z-0" data-testid="projects-map">
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <WmsLayers activeLayers={wmsLayers} />
      <FitToMarkers points={points} />
      <FlyToSelected target={selected ? [selected.latitude, selected.longitude] : null} />
      <ClickCapture active={placingActive} onPick={onPlace} />
      <FellingAreasLayer areas={visibleAreas} selectedId={null} onSelect={(areaId) => { const area = visibleAreas.find((a) => a.id === areaId); if (area && !placingActive) onSelect(area.project_id); }} />
      {visibleBoundaries.map((boundary) => {
        const project = projects.find((p) => p.id === boundary.project_id);
        const color = STATUS_PIN_COLORS[project?.status] || STATUS_PIN_COLORS.not_started;
        const isSelected = boundary.project_id === selectedId;
        return (
          <GeoJSON
            key={`${boundary.project_id}-${project?.status}-${isSelected}`}
            data={boundary.geojson}
            style={() => ({ color, weight: isSelected ? 4 : 2.5, fillColor: color, fillOpacity: isSelected ? 0.3 : 0.15 })}
            eventHandlers={{ click: () => { if (!placingActive) onSelect(boundary.project_id); } }}
          />
        );
      })}
      {located.map((project) => (
        <Marker
          key={project.id}
          position={[project.latitude, project.longitude]}
          icon={pinIcon(STATUS_PIN_COLORS[project.status] || STATUS_PIN_COLORS.not_started, project.id === selectedId)}
          draggable
          ref={(ref) => { markerRefs.current[project.id] = ref; }}
          eventHandlers={{
            click: () => onSelect(project.id),
            dragend: (e) => onMove(project, e.target.getLatLng())
          }}
        >
          <Popup>
            <div className="min-w-[200px] space-y-1" data-testid={`map-popup-${project.id}`}>
              <p className="font-semibold text-[#1F3D2B] leading-tight">{project.name}</p>
              <p className="text-xs text-gray-600">{clientNames[project.client_id] || '—'}{project.cadastral_number ? ` · ${project.cadastral_number}` : ''}</p>
              <p className="text-xs"><span className="inline-block w-2.5 h-2.5 rounded-full mr-1 align-middle" style={{ background: STATUS_PIN_COLORS[project.status] || STATUS_PIN_COLORS.not_started }} />{projectStatusLabel(t, project.status)} · progress {project.progress}%</p>
              {project.area_ha ? <p className="text-xs text-gray-600">Platība: {project.area_ha} ha</p> : null}
              {project.next_step ? <p className="text-xs text-blue-800 italic">Tālāk: {project.next_step}</p> : null}
              <button type="button" onClick={() => onOpenProject(project.id)} className="mt-1 text-xs font-semibold text-white bg-[#1F3D2B] px-3 py-1.5 rounded-md hover:bg-[#2a5039]" data-testid={`map-popup-open-${project.id}`}>Atvērt objektu</button>
            </div>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
};

export default ProjectsLeafletMap;
